/**
 * Select Component
 * Dropdown selector using Radix UI
 * 
 * @author Varun Pandey
 */

import React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  /** When true, error ring is still applied but error message is not rendered (e.g. when showing errors via toast). */
  hideErrorText?: boolean;
  disabled?: boolean;
  className?: string;
}

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    options,
    value,
    onChange,
    placeholder = 'Select',
    label,
    error,
    hideErrorText = false,
    disabled,
    className,
  },
  ref
) {
  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="mb-2 block text-xs font-medium text-[#8E8E93] uppercase tracking-wider">
          {label}
        </label>
      )}
      <SelectPrimitive.Root value={value} onValueChange={onChange} disabled={disabled}>
        <SelectPrimitive.Trigger
          ref={ref}
          className={cn(
            'flex h-12 w-full items-center justify-between rounded-[0.75rem] bg-transparent border border-[#3A3A3C] px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-[#0B0B0D] disabled:cursor-not-allowed disabled:opacity-50',
            error && 'ring-2 ring-destructive',
            !value && 'text-[#8E8E93]'
          )}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon>
            <ChevronDown className="h-4 w-4 text-[#8E8E93]" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className="relative z-50 min-w-[8rem] rounded-[0.75rem] bg-[#2C2C2E] text-white shadow-md"
            position="popper"
            sideOffset={4}
          >
            <SelectPrimitive.Viewport className="max-h-[200px] sm:max-h-[250px] md:max-h-[300px] overflow-y-auto p-1">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  className={cn(
                    'relative flex cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none hover:bg-[#3A3A3C] focus:bg-[#3A3A3C] data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                  )}
                >
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="absolute right-2 left-auto flex items-center">
                    <Check className="h-4 w-4" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      {error && !hideErrorText && (
        <p className="mt-1 text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
});
